import { type ArticleFilterDto, type ArticleView, type AuthorsByAlias, type SearchArticleMeta, searchArticles } from "@serophin/nottldr-shared";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { ArticleListSeed } from "./seedTypes";

type ListState = { metas: SearchArticleMeta[]; authors: AuthorsByAlias; cursor: string | null; hasMore: boolean };

const EMPTY: ListState = { metas: [], authors: {}, cursor: null, hasMore: true };

const fromSeed = (seed: ArticleListSeed): ListState => ({
  metas: seed.metas,
  authors: seed.authors,
  cursor: seed.nextCursor,
  hasMore: seed.nextCursor !== null,
});

export type SeededArticles = {
  metas: SearchArticleMeta[];
  authors: AuthorsByAlias;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

type Query = { view: ArticleView; filter?: ArticleFilterDto; limit?: number };

type Options = Query & { filterKey: string; seed?: ArticleListSeed };

/** A ref that always holds the value of the latest render, for callbacks that must not change identity. */
const useLatest = <T>(value: T): { current: T } => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

/** The page after `from` (or the first page, replacing it) as a list state. */
const nextState = async (query: Query, from: ListState, append: boolean): Promise<ListState> => {
  const page = await searchArticles({ ...query, cursor: from.cursor ?? undefined });
  return {
    metas: append ? [...from.metas, ...page.metas] : page.metas,
    authors: { ...from.authors, ...page.authors },
    cursor: page.nextCursor,
    hasMore: page.nextCursor !== null,
  };
};

/**
 * An infinite list over the article search that starts from the prerendered first page when there is one for
 * `filterKey`, and from the API otherwise. It starts over when `filterKey` changes. Answers of a load that a
 * newer one has replaced are dropped.
 */
export const useSeededArticles = ({ view, filter, filterKey, seed, limit }: Options): SeededArticles => {
  const [state, setState] = useState<ListState>(() => (seed ? fromSeed(seed) : EMPTY));
  const [isLoading, setIsLoading] = useState(seed === undefined);
  const generation = useRef(0);
  const seededKey = useRef<string | undefined>(seed ? filterKey : undefined);
  const query = useLatest<Query>({ view, filter, limit });

  const load = useCallback(async (from: ListState, append: boolean) => {
    const current = ++generation.current;
    setIsLoading(true);
    try {
      const next = await nextState(query.current, from, append);
      if (current === generation.current) {
        setState(next);
      }
    } catch (error) {
      console.error("Failed to load articles", error);
      if (current === generation.current) {
        setState((previous) => ({ ...previous, hasMore: false }));
      }
    } finally {
      if (current === generation.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const skip = seededKey.current === filterKey;
    seededKey.current = undefined;
    if (!skip) {
      void load(EMPTY, false);
    }
  }, [filterKey]);

  const loadMore = () => {
    if (!isLoading && state.hasMore) {
      void load(state, true);
    }
  };

  return { metas: state.metas, authors: state.authors, isLoading, hasMore: state.hasMore, loadMore };
};
